import React, { useEffect, useState } from "react";
import { Star, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import ShowReasoning from "@/components/ShowReasoning";

export default function ReputationManager() {
  const [reviews, setReviews] = useState([]);
  const [sources, setSources] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [response, setResponse] = useState(null);

  useEffect(() => {
    api.get("/reputation/reviews").then(r=>setReviews(r.data));
    api.get("/reputation/sources").then(r=>setSources(r.data));
  }, []);

  const drafts = async (r) => {
    setBusyId(r.id);
    try {
      const data = await aiJob("/reputation/draft-response", { review_id: r.id, review_text: r.text, rating: r.rating, reviewer: r.reviewer });
      setResponse({ id: r.id, text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer });
      toast.success("Response drafted");
    } catch { toast.error("Failed"); }
    finally { setBusyId(null); }
  };

  const avg = reviews.length ? (reviews.reduce((s,r)=>s+r.rating,0)/reviews.length).toFixed(1) : "—";
  const negative = reviews.filter(r=>r.rating<=3).length;

  const cols = [
    { key: "platform", label: "Platform" },
    { key: "rating", label: "Rating", render: (r)=><span className="text-amber-500 font-semibold">{"★".repeat(r.rating)}<span className="opacity-30">{"★".repeat(5-r.rating)}</span></span> },
    { key: "reviewer", label: "Reviewer" },
    { key: "text", label: "Review", render: (r)=>r.text.slice(0,80)+"…" },
    { key: "date", label: "Date" },
    { key: "actions", label: "", sortable: false, render: (r)=>(
      <button onClick={()=>drafts(r)} disabled={busyId===r.id} className="text-xs px-2 py-1 rounded bg-brand-primary text-white hover:bg-[#35096B] disabled:opacity-50 inline-flex items-center gap-1" data-testid={`draft-resp-${r.id}`}>
        {busyId===r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Draft
      </button>
    )},
  ];

  return (
    <>
      <PageHeader eyebrow="Module 25" title="Reputation Manager"
        subtitle="Multi-platform review monitoring, AI HIPAA-safe response drafting, referral source tracking." testid="rep-page" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiCard icon={Star} label="Avg Rating" value={avg} testid="kpi-avg" />
        <KpiCard label="Negative reviews" value={negative} hint="≤ 3 stars" accent testid="kpi-neg" />
        <KpiCard label="Total platforms" value="4" testid="kpi-plat" />
      </div>

      <SectionCard title="Recent Reviews" testid="reviews-section">
        <DataTable data={reviews} columns={cols} searchKeys={["platform","reviewer","text"]} testid="reviews-table" />
      </SectionCard>

      {response && (
        <SectionCard title="Drafted Response" testid="resp-card">
          <AIOutputDisclaimer />
          <pre className="text-sm whitespace-pre-wrap leading-relaxed bg-brand-bg p-3 rounded-md max-h-72 overflow-y-auto" data-testid="rep-resp-text">{response.text}</pre>
          {response.verification_footer && (
            <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="rep-verification-footer">{response.verification_footer}</div>
          )}
          <ShowReasoning reasoning={response.reasoning} score={response.score} testid="rep-reasoning" />
          <AIFeedback module="reputation-manager" outputId={response.id} outputType="review_response" testid="rep-feedback" />
        </SectionCard>
      )}

      <div className="mt-6">
        <SectionCard title="Referral Source Mix (30d)" testid="sources">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {sources.map(s=>(
              <div key={s.source} className="p-3 bg-brand-bg rounded-md">
                <div className="eyebrow">{s.source}</div>
                <div className="text-2xl font-semibold text-brand-primary mt-1">{s.new_patients_30d}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </>
  );
}
